// ------------------------------------------------------------
// Google Analytics 4 + Meta Pixel with e-commerce events.
// Set in the frontend .env (leave empty to switch off):
//   REACT_APP_GA4_ID=G-XXXXXXXXXX
//   REACT_APP_META_PIXEL_ID=123456789012345
// Product ids sent here match the product feed (item_group_id = product
// id), so Meta / Google can link events to catalogue items for ads.
// ------------------------------------------------------------
const GA4 = process.env.REACT_APP_GA4_ID;
const PIXEL = process.env.REACT_APP_META_PIXEL_ID;
let started = false;

const loadScript = (src) => {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
};

export const initAnalytics = () => {
  if (started || typeof window === 'undefined') return;
  started = true;
  if (GA4) {
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA4}`);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
    };
    window.gtag('js', new Date());
    window.gtag('config', GA4, { send_page_view: false, currency: 'INR' });
  }
  if (PIXEL) {
    // Meta Pixel base code (written out so it passes the linter)
    if (!window.fbq) {
      const fbq = function fbq() {
        if (fbq.callMethod) {
          fbq.callMethod.apply(fbq, arguments); // eslint-disable-line prefer-rest-params
        } else {
          fbq.queue.push(arguments); // eslint-disable-line prefer-rest-params
        }
      };
      window.fbq = fbq;
      if (!window._fbq) window._fbq = fbq;
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = '2.0';
      fbq.queue = [];
      loadScript('https://connect.facebook.net/en_US/fbevents.js');
    }
    window.fbq('init', PIXEL);
  }
};

const ga = (...args) => window.gtag && GA4 && window.gtag(...args);
const fb = (...args) => window.fbq && PIXEL && window.fbq(...args);

const gaItem = (p, extra = {}) => ({
  item_id: String(p._id || p.product || p.id),
  item_name: p.name,
  item_brand: p.brand || 'Mithralaya',
  item_category: p.subCategory || p.category || undefined,
  item_variant: extra.size || p.size || undefined,
  price: Number(extra.price ?? p.price) || undefined,
  quantity: Number(extra.qty ?? p.qty) || 1
});

export const trackPageView = (path) => {
  ga('event', 'page_view', { page_path: path, page_location: window.location.href, page_title: document.title });
  fb('track', 'PageView');
};

export const trackViewItem = (p) => {
  if (!p?._id) return;
  ga('event', 'view_item', { currency: 'INR', value: Number(p.price) || 0, items: [gaItem(p)] });
  fb('track', 'ViewContent', {
    content_ids: [String(p._id)],
    content_type: 'product_group',
    content_name: p.name,
    content_category: p.subCategory,
    value: Number(p.price) || 0,
    currency: 'INR'
  });
};

export const trackViewItemList = (listName, products = []) => {
  if (!products.length) return;
  ga('event', 'view_item_list', { item_list_name: listName, items: products.slice(0, 20).map((p) => gaItem(p)) });
};

export const trackSearch = (term) => {
  if (!term) return;
  ga('event', 'search', { search_term: term });
  fb('track', 'Search', { search_string: term });
};

export const trackAddToWishlist = (p) => {
  ga('event', 'add_to_wishlist', { currency: 'INR', value: Number(p?.price) || 0, items: p?.name ? [gaItem(p)] : [] });
  fb('track', 'AddToWishlist', { content_ids: [String(p?._id || p)], content_type: 'product_group' });
};

export const trackAddToCart = (p, { size, qty = 1, price } = {}) => {
  const value = (Number(price ?? p.price) || 0) * qty;
  ga('event', 'add_to_cart', { currency: 'INR', value, items: [gaItem(p, { size, qty, price })] });
  fb('track', 'AddToCart', {
    content_ids: [String(p._id)],
    content_type: 'product_group',
    content_name: p.name,
    value,
    currency: 'INR'
  });
};

export const trackBeginCheckout = (items = [], value = 0) => {
  ga('event', 'begin_checkout', { currency: 'INR', value, items: items.map((i) => gaItem(i.product || i, { size: i.size, qty: i.qty, price: i.price })) });
  fb('track', 'InitiateCheckout', {
    content_ids: items.map((i) => String(i.product?._id || i.product || i._id)),
    content_type: 'product_group',
    num_items: items.reduce((s, i) => s + (Number(i.qty) || 1), 0),
    value,
    currency: 'INR'
  });
};

// Fires once per order (safe to call again on refresh)
export const trackPurchase = (order) => {
  if (!order?.orderId) return;
  const key = `mithralaya.tracked.${order.orderId}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch (e) {
    /* ignore */
  }
  const items = (order.orderItems || []).map((i) => ({
    item_id: String(i.product?._id || i.product),
    item_name: i.name,
    item_variant: i.size,
    price: Number(i.price) || 0,
    quantity: Number(i.qty) || 1
  }));
  ga('event', 'purchase', {
    transaction_id: order.orderId,
    currency: 'INR',
    value: Number(order.totalPrice) || 0,
    shipping: Number(order.shippingPrice) || 0,
    tax: Number(order.taxPrice) || 0,
    coupon: order.coupon?.code,
    payment_type: order.paymentMethod,
    items
  });
  fb(
    'track',
    'Purchase',
    {
      content_ids: items.map((i) => i.item_id),
      content_type: 'product_group',
      num_items: items.reduce((s, i) => s + i.quantity, 0),
      value: Number(order.totalPrice) || 0,
      currency: 'INR'
    },
    { eventID: order.orderId } // lets Meta de-duplicate if you add server-side events later
  );
};
